import React from "react";
import { Smartphone } from "lucide-react";
import CheckList from "components/checkList/CheckList";
import FeatureBadge from "components/featureBadge/FeatureBadge";
import UseContext from "hooks/useContext";
import qrCodeImage from "assets/images/qrcode-app.svg";

const appStoreLinks = {
    ios: process.env.REACT_APP_IOS_STORE_URL_PRO || "https://apps.apple.com/app/jobbiz-pro",
    android:
        process.env.REACT_APP_ANDROID_STORE_URL_PRO ||
        "https://play.google.com/store/apps/details?id=com.mycompany.jobbizpro",
};

export const useAppProLabel = () => {
    const { language } = UseContext();
    return (key, fallback) => language.getValue(`appPro.${key}`, fallback);
};

const AppleLogo = () => (
    <svg className="h-5 w-5 shrink-0" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
);

const AndroidLogo = () => (
    <svg
        className="h-5 w-5 shrink-0"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        viewBox="0 0 24 24"
        aria-hidden="true"
    >
        <path d="M12 8V4H8" />
        <rect width="16" height="12" x="4" y="8" rx="2" />
        <path d="M2 14h2" />
        <path d="M20 14h2" />
        <path d="M15 13v2" />
        <path d="M9 13v2" />
    </svg>
);

// A store link drawn as the SECONDARY button (components/button): a link, so it cannot be a <button>.
const StoreLink = ({ href, logo, children }) => (
    <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="flex h-12 flex-1 items-center justify-center gap-2 rounded-md border border-gray-100 bg-white px-4 text-base text-gray-700 hover:bg-gray-100 active:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500"
    >
        {logo}
        {children}
    </a>
);

/**
 * The pitch, as on the job templates' introduction card (TemplatesIntro): a phone tile, a badge,
 * a title, one line, then the benefits under the temp worker app's badges (CheckList).
 */
export const AppProPitch = ({ titleId }) => {
    const label = useAppProLabel();
    return (
        <div className="flex flex-col gap-4">
            <div className="corner-smooth-12 flex h-11 w-11 shrink-0 items-center justify-center bg-yellow-50">
                <Smartphone size={22} color="#FF9600" aria-hidden="true" />
            </div>
            <div className="flex flex-col gap-1">
                <div className="mb-1">
                    <FeatureBadge>{label("eyebrow", "Application mobile")}</FeatureBadge>
                </div>
                <h2 id={titleId} className="text-lg font-semibold leading-6 tracking-[-0.3px] text-gray-900">
                    {label("title", "Jobbiz Pro, vos missions dans la poche")}
                </h2>
                <p className="text-sm leading-5 text-gray-600">
                    {label(
                        "description",
                        "L'essentiel de votre espace entreprise, sur votre téléphone, où que vous soyez."
                    )}
                </p>
            </div>
            <CheckList
                items={[
                    { text: label("benefitMissions", "Créez et suivez vos missions"), badge: "check" },
                    { text: label("benefitTimesheets", "Validez les relevés d'heures en quelques secondes"), badge: "check" },
                    { text: label("benefitContracts", "Signez vos contrats sans ordinateur"), badge: "check" },
                ]}
            />
        </div>
    );
};

/** The QR code on a grey card, then the two stores side by side. */
export const AppProDownload = () => {
    const label = useAppProLabel();
    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col items-center gap-3 rounded-2xl border border-gray-100 bg-gray-50 p-5">
                <img
                    src={qrCodeImage}
                    alt={label("qrAlt", "QR code pour télécharger l'application Jobbiz Pro")}
                    className="h-44 w-44 rounded-xl border border-gray-100 bg-white p-2"
                />
                <p className="text-center text-sm leading-5 text-gray-600">
                    {label("qrHint", "Scannez avec l'appareil photo de votre téléphone")}
                </p>
            </div>
            <div className="flex items-center gap-3" aria-hidden="true">
                <span className="h-px flex-1 bg-gray-100" />
                <span className="text-xs leading-4 text-gray-500">{label("or", "ou")}</span>
                <span className="h-px flex-1 bg-gray-100" />
            </div>
            <div className="flex gap-3">
                <StoreLink href={appStoreLinks.ios} logo={<AppleLogo />}>
                    {label("appStore", "App Store")}
                </StoreLink>
                <StoreLink href={appStoreLinks.android} logo={<AndroidLogo />}>
                    {label("googlePlay", "Google Play")}
                </StoreLink>
            </div>
        </div>
    );
};
